/**
 * AssignmentActivityPage — dedicated page showing what an assigned
 * professional did on THIS corporate's companies (company-scoped audit rows;
 * the backend enforces the privacy boundary — nothing outside the
 * assignment's company scope, and no IP/device metadata).
 *
 * Route: /manage-collaborators/:assignmentId/activity (CorporateRoute).
 * Breadcrumb navigates back to Manage Collaborators.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiChevronRight, FiActivity } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import { professionalAssignmentAPI } from '../services/api';
import { ROUTES } from '../constants/constants';

const MUTED = '#7a7060';
const TEXT = '#08111f';
const BORDER = '#e7e2d8';

// "draft.created" → "Draft created"
const humanizeAction = (action) => {
  const cleaned = String(action || '').replace(/\./g, ' ').replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const formatWhen = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

export default function AssignmentActivityPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const professionalName = assignment?.professional?.fullName || 'Professional';

  const loadPage = useCallback(async (cursor) => {
    const res = await professionalAssignmentAPI.getAssignmentActivity(assignmentId, { cursor, limit: 40 });
    const data = res?.data || {};
    return { rows: data.rows || [], nextCursor: data.nextCursor || null };
  }, [assignmentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [detailsRes, first] = await Promise.all([
          professionalAssignmentAPI.getAssignmentDetails(assignmentId).catch(() => null),
          loadPage(),
        ]);
        if (cancelled) return;
        if (detailsRes?.success) setAssignment(detailsRes.data?.assignment || null);
        setRows(first.rows);
        setNextCursor(first.nextCursor);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load activity.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assignmentId, loadPage]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const page = await loadPage(nextCursor);
      setRows((prev) => [...prev, ...page.rows]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err?.message || 'Could not load more activity.');
    } finally {
      setLoadingMore(false);
    }
  };

  const scopeLabel = assignment
    ? assignment.assignmentScope === 'all_companies'
      ? 'all your companies'
      : `${(assignment.assignedCompanies || []).length} assigned ${(assignment.assignedCompanies || []).length === 1 ? 'company' : 'companies'}`
    : 'your companies';

  return (
    <AppLayout pageTitle="Professional Activity">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[12px] mb-3" style={{ color: MUTED }}>
        <button
          type="button"
          onClick={() => navigate(ROUTES.MANAGE_COLLABORATORS)}
          className="hover:underline"
          style={{ color: '#145f5f' }}
        >
          Manage Collaborators
        </button>
        <FiChevronRight size={13} />
        <span style={{ color: TEXT }}>{professionalName} — activity</span>
      </nav>

      <PageHeader
        title={`${professionalName} — activity`}
        subtitle={`What this professional did on ${scopeLabel} (last ~90 days). Only actions on your companies are shown.`}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.MANAGE_COLLABORATORS)}>
            Back to collaborators
          </Button>
        }
      />

      <div className="bg-white border rounded-[16px] p-6" style={{ borderColor: BORDER }}>
        {error && (
          <div className="mb-4 px-3 py-2 rounded-lg text-[12px]" style={{ background: '#f2e9e6', color: '#9a564a' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-[12.5px] py-12 text-center" style={{ color: MUTED }}>Loading activity…</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <FiActivity size={22} className="mx-auto mb-2" style={{ color: '#b3a892' }} />
            <div className="text-[12.5px]" style={{ color: MUTED }}>
              No recorded activity on your companies yet.
            </div>
            <div className="text-[11px] mt-1" style={{ color: '#9a9080' }}>
              Activity appears here once the professional works on a company covered by this assignment.
            </div>
          </div>
        ) : (
          <div>
            <ul className="m-0 p-0 list-none divide-y" style={{ borderColor: '#eee6d8' }}>
              {rows.map((row) => (
                <li key={row._id} className="py-3" style={{ borderColor: '#eee6d8' }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium" style={{ color: TEXT }}>
                      {humanizeAction(row.action)}
                    </span>
                    {row.success === false && (
                      <span className="text-[10.5px] font-semibold px-2 py-[2px] rounded-full" style={{ background: '#f2e9e6', color: '#9a564a' }}>
                        Failed
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] mt-[2px]" style={{ color: MUTED }}>
                    {[
                      row.details?.documentTitle,
                      row.targetModel === 'Company' ? row.targetName : null,
                      row.details?.companyCin,
                    ].filter(Boolean).join(' · ') || row.targetName || ''}
                    {' · '}
                    {formatWhen(row.createdAt)}
                  </div>
                </li>
              ))}
            </ul>

            {nextCursor && (
              <div className="pt-4 border-t" style={{ borderColor: '#eee6d8' }}>
                <Button variant="outline" size="sm" onClick={loadMore} loading={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
